import { Component, OnInit, OnDestroy, ViewEncapsulation, NgZone, ChangeDetectorRef } from '@angular/core';
import { Subscription } from 'rxjs';
import { ReauthService } from '../../../services/reauth.service';
import { ApiServiceService } from '../../../services/api-service.service';

/**
 * ReauthModalComponent — modale IN-PLACE di ri-autenticazione (password-only).
 *
 * Mostrata sopra la pagina corrente quando la sessione scade (401), senza reload.
 * Lo username è fissato all'utente corrente: si chiede solo la password. Al successo
 * il token viene rinnovato e le richieste fallite rigiocate (vedi ReauthService +
 * AuthorizationInterceptor). "Sign out instead" ricade sul logout classico.
 *
 * La chiamata login() vive qui (non in ReauthService) per evitare il ciclo DI
 * interceptor → service → HttpClient → interceptor.
 */
@Component({
  selector: 'app-reauth-modal',
  templateUrl: './reauth-modal.component.html',
  styleUrls: ['./reauth-modal.component.scss'],
  // None: ion-modal teleporta il contenuto fuori dallo scope del componente,
  // quindi gli stili devono essere globali (tutti annidati sotto .reauth per non leakare).
  encapsulation: ViewEncapsulation.None
})
export class ReauthModalComponent implements OnInit, OnDestroy {

  isVisible = false;
  username = '';
  password = '';
  showPassword = false;
  loading = false;
  errorMsg: string | null = null;

  private subs: Subscription[] = [];

  constructor(
    private reauth: ReauthService,
    private api: ApiServiceService,
    private zone: NgZone,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.subs.push(
      this.reauth.visible$.subscribe(visible => {
        // zone.run + detectChanges: la richiesta che scade puo' arrivare da fuori
        // la zona Angular, e allora il binding [isOpen] non viene aggiornato mentre
        // la classe sul body - che si scrive a mano, qui sotto - viene applicata
        // lo stesso. Il risultato e' la pagina sfocata SENZA la finestra di login:
        // riprodotto, modale 0x0 e classe `overlay-hidden`. Forzare qui il
        // rilevamento modifiche rende la modale indipendente da chi la apre.
        this.zone.run(() => {
          this.isVisible = visible;
          // Sfoca il modale sottostante (il backdrop-filter dell'overlay non sfoca un
          // ion-modal già composto): classe sul body → regola in global.scss.
          document.body.classList.toggle('reauth-active', visible);
          if (visible) {
            // Reset pulito ad ogni apertura
            this.username = this.reauth.pinnedUsername;
            this.password = '';
            this.errorMsg = null;
            this.loading = false;
            this.showPassword = false;
          }
          this.cdr.detectChanges();
        });
      })
    );
  }

  ngOnDestroy(): void {
    this.subs.forEach(s => s.unsubscribe());
    document.body.classList.remove('reauth-active');
  }

  submit(): void {
    if (!this.password || this.loading) { return; }
    this.loading = true;
    this.errorMsg = null;

    this.api.login(this.username, this.password).subscribe({
      next: (res: any) => {
        if (res && res.code == 0 && res.result && res.result.token) {
          // Successo: rinnova il token + rigioca le richieste in coda. La modale si chiude.
          this.reauth.completeReauth(res.result.token);
        } else {
          this.loading = false;
          this.errorMsg = (res && res.msg)
            ? res.msg
            : 'Incorrect password. Please try again.';
        }
      },
      error: () => {
        this.loading = false;
        this.errorMsg = 'Unable to reach the server. Please try again.';
      }
    });
  }

  signOut(): void {
    this.reauth.cancelToLogin();
  }

  togglePassword(): void {
    this.showPassword = !this.showPassword;
  }

  /**
   * A presentazione avvenuta dell'ion-modal, mette a fuoco il campo password.
   * (Ionic già mette a fuoco il primo elemento focusabile, ma lo forziamo per sicurezza.)
   * Query sul DOM invece di @ViewChild: il contenuto dell'ion-modal è proiettato.
   */
  onPresented(): void {
    // Chiudi eventuali spinner "wait..." (ion-loading) rimasti attivi: l'azione che ha
    // scatenato il 401 resta in pending sulla re-auth, quindi il suo loading è orfano e
    // non deve coprire la modale. ion-loading è teleportato in cima → query sul document.
    document.querySelectorAll('ion-loading').forEach((el: any) => {
      try { el.dismiss(); } catch (e) { /* no-op */ }
    });
    // Focus sul campo password.
    setTimeout(() => {
      const input = document.querySelector('ion-modal.reauth-modal-host input') as HTMLInputElement | null;
      input?.focus();
    }, 50);
  }
}
